/**
 * Platform-agnostic API client.
 *
 * Why this exists
 * ---------------
 * StyleGenome targets Web (now) + Expo/React Native (future). To keep the
 * backend contract reusable, ALL frontend network calls should funnel through
 * this thin wrapper. It hides:
 *
 *   - The base URL resolution (env var on web, app config on RN)
 *   - The auth mechanism (cookie on web; Authorization: Bearer on RN)
 *   - The fetch implementation (browser fetch on web; the same on RN via Expo)
 *
 * Concretely, on web today we set `credentials: 'include'` so the
 * `session_token` HttpOnly cookie is forwarded. On RN tomorrow, the same
 * client will read a token from secure storage and attach
 * `Authorization: Bearer <token>` — no caller changes needed.
 *
 * No browser-only APIs are imported here (no `window`, no `document`,
 * no `localStorage`, no `URLSearchParams` instance methods). This file
 * is safe to import from RN.
 */

// Resolve base URL from whatever env mechanism the runtime exposes.
// On Web (CRA): window.__SG_API_BASE__.
// On Expo: Constants.expoConfig.extra.backendUrl (consumer can pass via setBaseUrl).
let _baseUrl =
  (typeof process !== 'undefined' && process.env && window.__SG_API_BASE__) || '';

// Auth strategy — cookie on web, bearer-token on native. Consumers can override.
let _authStrategy = 'cookie'; // 'cookie' | 'bearer'
let _bearerToken = null;

// Session-invalidation hook — fired on 401 (dead session) or on the
// hard-block 403 (account banned/deleted mid-session). NOT fired for
// suspended-account 403s: those keep read-only browsing by design.
let _onAuthFailure = null;
const HARD_BLOCK_DETAIL = 'This account is no longer active.';

const maybeNotifyAuthFailure = (status, payload, headers) => {
  if (!_onAuthFailure) return;
  const detail = payload && payload.detail;
  // Structured code first (X-Error-Code) — English copy is only a fallback.
  const code = headers && typeof headers.get === 'function' ? headers.get('x-error-code') : null;
  const hardBlock = status === 403 && (code === 'account_inactive' || detail === HARD_BLOCK_DETAIL);
  if (status === 401 || hardBlock) {
    try { _onAuthFailure(status, detail); } catch { /* observer must not break calls */ }
  }
};

export const apiConfig = {
  setBaseUrl(url) { _baseUrl = (url || '').replace(/\/$/, ''); },
  getBaseUrl() { return _baseUrl; },
  setAuthStrategy(strategy) {
    if (strategy !== 'cookie' && strategy !== 'bearer') {
      throw new Error(`Unknown auth strategy: ${strategy}`);
    }
    _authStrategy = strategy;
  },
  setBearerToken(token) { _bearerToken = token || null; },
  setAuthFailureHandler(fn) { _onAuthFailure = typeof fn === 'function' ? fn : null; },
};

const buildUrl = (path, query) => {
  const base = _baseUrl.replace(/\/$/, '');
  const rel = path.startsWith('/') ? path : `/${path}`;
  if (!query) return `${base}${rel}`;
  const qs = Object.entries(query)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');
  return qs ? `${base}${rel}?${qs}` : `${base}${rel}`;
};

const buildHeaders = (extra) => {
  const headers = { 'Content-Type': 'application/json', ...(extra || {}) };
  if (_authStrategy === 'bearer' && _bearerToken) {
    headers.Authorization = `Bearer ${_bearerToken}`;
  }
  return headers;
};

const request = async (method, path, { body, query, headers, signal } = {}) => {
  const init = {
    method,
    headers: buildHeaders(headers),
    signal,
  };
  if (_authStrategy === 'cookie') init.credentials = 'include';
  if (body !== undefined && body !== null) init.body = JSON.stringify(body);

  let res;
  try {
    res = await fetch(buildUrl(path, query), init);
  } catch (e) {
    const wrapped = new Error('Network request failed');
    wrapped.cause = e;
    wrapped.kind = 'network';
    throw wrapped;
  }

  let payload = null;
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) {
    try { payload = await res.json(); } catch { payload = null; }
  } else {
    try { payload = await res.text(); } catch { payload = null; }
  }

  if (!res.ok) {
    maybeNotifyAuthFailure(res.status, payload, res.headers);
    const err = new Error((payload && (payload.detail || payload.message)) || `HTTP ${res.status}`);
    err.status = res.status;
    err.data = payload;
    throw err;
  }
  return payload;
};

/**
 * Multipart / FormData upload — used for image + PDF attachments.
 *
 * Do NOT set `Content-Type` manually: the browser must generate the
 * `multipart/form-data; boundary=...` header for the file to be parsed
 * correctly. Auth is still forwarded (cookie on web, bearer on RN).
 */
const uploadRequest = async (path, formData, { query, signal } = {}) => {
  const init = {
    method: 'POST',
    body: formData,
    signal,
    headers: {},
  };
  if (_authStrategy === 'cookie') init.credentials = 'include';
  if (_authStrategy === 'bearer' && _bearerToken) {
    init.headers.Authorization = `Bearer ${_bearerToken}`;
  }
  let res;
  try {
    res = await fetch(buildUrl(path, query), init);
  } catch (e) {
    const wrapped = new Error('Network request failed');
    wrapped.cause = e;
    wrapped.kind = 'network';
    throw wrapped;
  }
  let payload = null;
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) {
    try { payload = await res.json(); } catch { payload = null; }
  } else {
    try { payload = await res.text(); } catch { payload = null; }
  }
  if (!res.ok) {
    maybeNotifyAuthFailure(res.status, payload, res.headers);
    const err = new Error((payload && (payload.detail || payload.message)) || `HTTP ${res.status}`);
    err.status = res.status;
    err.data = payload;
    throw err;
  }
  return payload;
};

/**
 * Auth-gated GET for binary/private assets — returns a browser blob URL
 * (created via URL.createObjectURL). Caller MUST revoke on unmount.
 */
const blobRequest = async (path, { query, signal } = {}) => {
  const init = {
    method: 'GET',
    headers: {},
    signal,
  };
  if (_authStrategy === 'cookie') init.credentials = 'include';
  if (_authStrategy === 'bearer' && _bearerToken) {
    init.headers.Authorization = `Bearer ${_bearerToken}`;
  }
  const res = await fetch(buildUrl(path, query), init);
  if (!res.ok) {
    maybeNotifyAuthFailure(res.status, null, res.headers);
    const err = new Error(`HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  return { blob, url };
};

export const api = {
  get:    (path, query, opts)       => request('GET',    path, { query, ...(opts || {}) }),
  post:   (path, body, opts)        => request('POST',   path, { body, ...(opts || {}) }),
  put:    (path, body, opts)        => request('PUT',    path, { body, ...(opts || {}) }),
  patch:  (path, body, opts)        => request('PATCH',  path, { body, ...(opts || {}) }),
  delete: (path, opts)              => request('DELETE', path, { ...(opts || {}) }),
  upload: (path, formData, opts)    => uploadRequest(path, formData, opts || {}),
  blob:   (path, opts)              => blobRequest(path, opts || {}),
};
